import React, { forwardRef, useCallback, useImperativeHandle, useState } from "react";
import { Typography, Table, Col, Spin } from "antd";
import axios from "api/axios";
import DpatSubIndicatorSection from "../setup/DpatSubIndicatorSection";
import DpatIndicatorCmsIntro from "../setup/DpatIndicatorCmsIntro";
import Comment from "components/Comments";
import CiResultDisplay from "./CiResultDisplay";
import { useDpatIndicatorDataSource } from "hooks/useDpatIndicatorDataSource";
import { getDisplayDataFromTableEntry } from "utils/dpatAssessmentStartData";
import {
  transformDpatTableRows,
  isEvidenceLinkUploaded,
} from "utils/renderDpatEvidenceLink";
import { getAttributeValue, getFileLinkIfExist } from "utils/utils";

const { Title } = Typography;

const DPAT_SLUG = "c1.0-1.2";
const MEETING_PROGRAM = "Ch38jUWJpUR";
const MEETING_DATE_ATTRIBUTE = "Ub0V9Z06aBc";
const MEETING_TYPE_ATTRIBUTE = "kghpIZgHFsT";
const MINUTES_DATA_ELEMENT = "LAe1t59jYNT";
const MEETING_TYPES = ["GA", "Budget Committee", "EC", "DMPCU"];
const SUPPORTING_MEETING_TYPES = ["EC", "Budget Committee", "DMPCU"];

const MEETING_COLUMNS = [
  { title: "Meeting", dataIndex: "meetingLabel", key: "meetingLabel" },
  { title: "Date Held", dataIndex: "date", key: "date" },
  { title: "AAP Approval Date", dataIndex: "decisions", key: "decisions" },
  { title: "Minutes", dataIndex: "minutes", key: "minutes" },
];

const TABLE_LINK_KEYS = ["minutes"];

function getMeetingType(meeting) {
  return getAttributeValue("DPAT | Meeting Type", meeting);
}

function filterMeetingsByType(meetings, types) {
  const typeSet = new Set(types);
  return (meetings || []).filter((item) => typeSet.has(getMeetingType(item)));
}

function formatMeetingLabel(type) {
  if (type === "DMPCU") return "M/DPCU";
  if (type === "GA") return "General Assembly";
  if (type === "EC") return "Executive Committee";
  return type;
}

function buildMinutesUrl(eventUid) {
  return `https://dddp.gov.gh/api/events/files?eventUid=${eventUid}&dataElementUid=${MINUTES_DATA_ELEMENT}`;
}

function hasApprovalDate(value) {
  return Boolean(value) && value !== "N/A";
}

function isApprovalOnOrBeforeCutoff(approvalDate, cutoffDate) {
  if (!hasApprovalDate(approvalDate)) {
    return false;
  }

  const parsed = new Date(approvalDate);
  return !Number.isNaN(parsed.getTime()) && parsed <= cutoffDate;
}

function toMeetingRow(meeting, reports, index) {
  const type = getMeetingType(meeting);
  const approvalDate = getAttributeValue("AAP Approval Date", meeting);
  const minuteLink = getFileLinkIfExist(
    reports,
    MINUTES_DATA_ELEMENT,
    meeting.trackedEntity,
  );

  return {
    key: meeting.trackedEntity || `${type}-${index}`,
    meeting: type,
    meetingLabel: formatMeetingLabel(type),
    date: getAttributeValue("DPAT | Meeting Date", meeting),
    documents: getAttributeValue("DPAT | Meeting Agenda", meeting),
    // Approval date is only meaningful for the GA meeting that approved the AAP.
    decisions: type === "GA" && hasApprovalDate(approvalDate) ? approvalDate : "",
    trackedEntity: meeting.trackedEntity,
    minutes: minuteLink ? buildMinutesUrl(minuteLink) : "Not Uploaded",
  };
}

function pickPreferredMeeting(meetingsOfType, reports) {
  if (!meetingsOfType?.length) {
    return null;
  }

  const withMinutes = meetingsOfType.find((meeting) =>
    Boolean(getFileLinkIfExist(reports, MINUTES_DATA_ELEMENT, meeting.trackedEntity)),
  );

  return withMinutes || meetingsOfType[0];
}

function buildIndicatorData(meetings, reports, indicatorYear) {
  const cutoffDate = new Date(`${indicatorYear}-10-31T23:59:59`);

  const gaMeetings = filterMeetingsByType(meetings, ["GA"]).filter((meeting) =>
    hasApprovalDate(getAttributeValue("AAP Approval Date", meeting)),
  );

  const supportingRows = SUPPORTING_MEETING_TYPES
    .map((type, index) => {
      const selected = pickPreferredMeeting(filterMeetingsByType(meetings, [type]), reports);
      return selected ? toMeetingRow(selected, reports, index) : null;
    })
    .filter(Boolean);

  const gaRows = gaMeetings.map((meeting, index) => toMeetingRow(meeting, reports, index));

  const rows = [...gaRows, ...supportingRows].sort(
    (a, b) => new Date(a.date) - new Date(b.date),
  );

  // Fulfilled only when GA AAP approval (on/before 31 Oct) has minutes
  // AND all three supporting meetings (EC, Budget Committee, M/DPCU) have minutes.
  const hasTimelyGaApproval = gaRows.some(
    (row) =>
      isApprovalOnOrBeforeCutoff(row.decisions, cutoffDate)
      && isEvidenceLinkUploaded(row.minutes),
  );

  const hasAllSupportingMeetingMinutes = SUPPORTING_MEETING_TYPES.every((type) =>
    supportingRows.some(
      (row) => row.meeting === type && isEvidenceLinkUploaded(row.minutes),
    ),
  );

  const fulfillment =
    hasTimelyGaApproval && hasAllSupportingMeetingMinutes
      ? "Fulfilled"
      : "Not Fulfilled";

  return {
    data: rows,
    fulfillment,
    indicatorYear,
  };
}

const AAPBudgetAproval = forwardRef(({
  year,
  districtId,
  hideComment,
  savedAssessmentData,
  assessmentStartLoading,
  isAssessmentStarted,
}, ref) => {
  const [liveData, setLiveData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const indicatorYear = Number(year) - 1;

  const displayData = isAssessmentStarted
    ? getDisplayDataFromTableEntry(savedAssessmentData)
    : liveData;

  useImperativeHandle(ref, () => ({
    getData: () => ({
      indicator: "CI1",
      area: "General Assembly Meetings and Approvals",
      data: displayData,
    }),
  }), [displayData]);

  const loadFromDhis2 = useCallback(() => {
    if (!districtId || !Number.isFinite(indicatorYear)) {
      setLiveData({
        data: [],
        fulfillment: "Not Fulfilled",
        indicatorYear: null,
      });
      return;
    }

    const startDate = `${indicatorYear}-01-01`;
    const endDate = `${indicatorYear}-12-31`;
    const typeFilter = MEETING_TYPES.map((type) => encodeURIComponent(type)).join(";");

    setIsLoading(true);

    axios
      .get(
        `/tracker/trackedEntities?orgUnit=${districtId}&program=${MEETING_PROGRAM}`
        + `&filter=${MEETING_DATE_ATTRIBUTE}:GE:${startDate}:LE:${endDate}`
        + `&filter=${MEETING_TYPE_ATTRIBUTE}:IN:${typeFilter}`
        + `&pageSize=5000`,
      )
      .then((result) => {
        const instances = result.data?.instances || [];
        const neededMeetings = filterMeetingsByType(instances, MEETING_TYPES);

        if (neededMeetings.length === 0) {
          setLiveData(buildIndicatorData([], [], indicatorYear));
          return;
        }

        const trackedEntityIds = new Set(neededMeetings.map((item) => item.trackedEntity));

        return axios
          .get(
            `/tracker/events?program=${MEETING_PROGRAM}&orgUnit=${districtId}`
            + `&startDate=${startDate}&endDate=${endDate}&pageSize=5000`,
          )
          .then((resp) => {
            const reports = (resp.data?.instances || []).filter((event) =>
              trackedEntityIds.has(event.trackedEntity),
            );
            setLiveData(buildIndicatorData(neededMeetings, reports, indicatorYear));
          });
      })
      .catch((err) => {
        console.error(err);
        setLiveData(buildIndicatorData([], [], indicatorYear));
      })
      .finally(() => setIsLoading(false));
  }, [districtId, indicatorYear]);

  useDpatIndicatorDataSource({
    isAssessmentStarted,
    savedAssessmentData,
    assessmentStartLoading,
    loadFromDhis2,
    applySnapshot: (snap) => {
      const snapshotData = getDisplayDataFromTableEntry(snap);
      if (snapshotData) {
        setLiveData(snapshotData);
      }
    },
    deps: [districtId, indicatorYear, loadFromDhis2],
  });

  return (
    <Spin spinning={assessmentStartLoading || isLoading} tip="Loading AAP Budget Approval data...">
      <Comment
        data={displayData}
        year={year}
        districtId={districtId}
        tableCommentedId={`c1.0-1.2-${year}`}
        hideComment={hideComment}
      >
        {({ renderCommentFeedbackFooter }) => (
          <DpatSubIndicatorSection code={DPAT_SLUG}>
            <DpatIndicatorCmsIntro year={year} slug={DPAT_SLUG} />

            <Col align="start">
              <CiResultDisplay slug={DPAT_SLUG} fulfillment={displayData?.fulfillment} />
            </Col>

            <Title level={4} style={{ marginTop: "20px" }}>
              Evidence of Approval of {year} Annual Action Plan and Budget
              {/* {Number.isFinite(indicatorYear) ? ` (${indicatorYear})` : ""} */}
            </Title>

            <Table
              columns={MEETING_COLUMNS}
              dataSource={transformDpatTableRows(displayData?.data || [], TABLE_LINK_KEYS)}
              pagination={false}
              bordered
              rowKey="key"
              locale={{
                emptyText: Number.isFinite(indicatorYear)
                  ? `No qualifying meeting evidence available for ${indicatorYear}`
                  : "No qualifying meeting evidence available",
              }}
            />

            {!hideComment && renderCommentFeedbackFooter()}
          </DpatSubIndicatorSection>
        )}
      </Comment>
    </Spin>
  );
});

export default AAPBudgetAproval;
